import React, { useState } from 'react';
import Icon from '@avtechsoftware/react-component-library/src/components/common/Icon/Icon';
import PropTypes from 'prop-types';
import ConditionalRender
  from '@avtechsoftware/react-component-library/src/components/common/ConditionalRender/ConditionalRender';
import ChannelService from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import { OperatorTypeDetails } from '@avtechsoftware/ra-javascript-utilities/src/types/OperatorTypes';
import BooleanChannel from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/BooleanChannel/BooleanChannel';
import {
  fromUserScaledNumber,
  renderValue,
} from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelValueRender';
import AppTypes from '@avtechsoftware/ra-javascript-utilities/src/types/AppTypes';
import { useSelector } from 'react-redux';
import ClassUtility from '../../../../utility/ClassUtility';
import ChannelSelector from '../../AlertForms/ChannelSelector/ChannelSelector';
import ThresholdSelect from '../../AlertForms/ThresholdSelect/ThresholdSelect';
import RaaDefaultButton from '../../../common/RaaDefaultButton/RaaDefaultButton';
import style from './QuickAlertForm.module.css';
import alertPropType from '../../../../utility/PropTypes/alertPropType';
import { selectByUuidOrMacAddress, selectSensorMap } from '../../../../redux/slices/deviceList/deviceListSlice';

function QuickAlertForm(props) {
  const {
    initialAlert,
    handleUpdateQuickAlerts,
    errorList,
  } = props;
  const sensorMap = useSelector(selectSensorMap);
  const getEditAlertObj = () => {
    const alertObj = ClassUtility.cloneObject(initialAlert);
    return alertObj;
  };

  const [editedAlert, setEditedAlert] = useState(getEditAlertObj);
  const [isDefaultAlert, setIsDefaultAlert] = useState(true);
  const [isEditingAlert, setIsEditingAlert] = useState(false);
  const [isFormLocked, setIsFormLocked] = useState(false);
  const [isDeviceSensor, setIsDeviceSensor] = useState(false);

  const { macAddress } = initialAlert.channelKey;
  const alertDevice = useSelector((state) => selectByUuidOrMacAddress(state, macAddress));
  const deviceName = alertDevice ? alertDevice.displayName : 'UNKNOWN DEVICE';
  const channelGroupList = sensorMap[macAddress] || [];
  const alertSensor = channelGroupList.find((group) => `${group.channelGroupMacAddress}_${group.channelGroupTypeId}_${group.channelGroupIndex}` === initialAlert.channelKey.channelGroupIdentifier);
  const sensorName = alertSensor ? alertSensor.label : 'UNKNOWN SENSOR';
  const isSubmitDisabled = (
    editedAlert.channelKey.channelType === 0
      && editedAlert.channelKey.channelIndex === 0
  );

  const handleUpdateChannelKey = (newChannelKey) => {
    const updatedAlert = ClassUtility.cloneObject(editedAlert);
    const channel = ChannelService.getChannelGroupChannelByAppType(
      newChannelKey.channelGroupType,
      newChannelKey.channelIndex,
      newChannelKey.channelType,
      AppTypes.CLOUD,
    );
    const initialOperatorTypeId = channel.channelType.thresholdOperators[0];
    updatedAlert.triggerCondition.operator = OperatorTypeDetails[initialOperatorTypeId].symbol;
    updatedAlert.triggerCondition.value = 0;
    updatedAlert.channelKey = newChannelKey;
    setEditedAlert(updatedAlert);
  };

  const handleUpdateDescription = (e) => {
    const updatedAlert = ClassUtility.cloneObject(editedAlert);
    updatedAlert.description = e.target.value;
    setEditedAlert(updatedAlert);
  };

  const renderAlertDetails = () => {
    const channel = ChannelService.getChannelGroupChannel(
      initialAlert.channelKey.channelGroupType,
      initialAlert.channelKey.channelIndex,
      initialAlert.channelKey.channelType,
    );
    const triggerValue = renderValue(
      fromUserScaledNumber(
        initialAlert.triggerCondition.value,
        initialAlert.channelKey.channelType,
        initialAlert.temperatureScale,
      ),
      initialAlert.channelKey,
      initialAlert.temperatureScale,
      initialAlert.customUnits,
    );
    const isBool = channel instanceof BooleanChannel;
    const alertIcon = isBool ? 'upload' : 'temperature-three-quarters';
    const boolAlertSummary = () => (
      <>
        Room Alert Account stops receiving sensor data from your device
        {' '}
        <b>{deviceName}</b>
      </>
    );
    const alertSummary = () => (
      <>
        <b>
          {sensorName}
        </b>
        , connected to
        {' '}
        <b>{deviceName}</b>
        , reports a temperature above
        {' '}
        <b>{triggerValue}</b>
      </>
    );
    return (
      <>
        <div className={`float-left ${style.targetSvg}`}>
          <Icon
            icon={alertIcon}
            size="2x"
            className="fa-fw text-muted mr-2"
          />
          {' '}
        </div>
        <div style={{ marginLeft: '1.5em' }}>
          <p>{isBool ? boolAlertSummary() : alertSummary()}</p>
        </div>
      </>
    );
  };

  const handleStartEditingAlert = () => {
    setIsEditingAlert(true);
    setIsFormLocked(false);
  };

  const handleClearChanges = () => {
    setIsEditingAlert(false);
    const originalAlert = getEditAlertObj();
    setEditedAlert(originalAlert);
    setIsFormLocked(true);
  };

  const handleSaveChanges = () => {
    handleUpdateQuickAlerts(editedAlert.id, editedAlert);
    if (isDefaultAlert) {
      setIsDefaultAlert(false);
    }
    setIsEditingAlert(false);
    setIsFormLocked(true);
    setIsDeviceSensor(false);
  };

  return (
    <>
      <ConditionalRender show={!isEditingAlert}>
        <div className="clearfix">
          <button
            type="button"
            className="float-right p-0 btn btn-link"
            onClick={handleStartEditingAlert}
          >
            <Icon
              icon="pencil"
              className="text-muted"
            />
          </button>
        </div>
        <h5 className="card-title">
          <b>{`Alert ${initialAlert.id}`}</b>
          {' '}
          {`"${initialAlert.description}" will trigger when...`}
        </h5>
        <ConditionalRender show={isDefaultAlert}>
          <div className="card-text">
            {renderAlertDetails()}
          </div>
        </ConditionalRender>
      </ConditionalRender>
      <ConditionalRender show={!isDefaultAlert || isEditingAlert}>
        <div>
          <form>
            <fieldset disabled={isFormLocked}>
              <ConditionalRender show={isEditingAlert}>
                <div className="form-group row">
                  <div className="col-12">
                    <input
                      className="form-control w-100"
                      value={editedAlert.description}
                      onChange={handleUpdateDescription}
                    />
                  </div>
                </div>
              </ConditionalRender>
              <ChannelSelector
                errorList={errorList}
                channelKey={editedAlert.channelKey}
                updateChannelKey={handleUpdateChannelKey}
                setIsDeviceSensor={setIsDeviceSensor}
                isQuickAlert
              />
              <ThresholdSelect
                errorList={errorList}
                alert={editedAlert}
                setAlert={setEditedAlert}
              />
              <ConditionalRender show={isDeviceSensor}>
                <div className="form-group form-inline row">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className="col-lg-2 control-label" />
                  <div className="col-auto col-lg-10">
                    <div className="alert alert-info">
                      <div className="float-left mr-1">
                        <Icon icon="info-circle" />
                      </div>
                      <div style={{ marginLeft: '1.5em' }}>
                        <p className="mb-0">
                          If you select
                          {' '}
                          <strong>Device</strong>
                          {' '}
                          from the
                          {' '}
                          <strong>Sensor</strong>
                          {' '}
                          drop-down menu, you may
                          alert
                          on these types of connections:
                        </p>
                        <p>
                          <strong>-Link Connection: </strong>
                          {' '}
                          Is your Room Alert device connected to Room Alert Link
                          software?
                          {' '}
                          <a target="_blank" rel="noopener noreferrer" href="https://avtech.com/articles/16289/">
                            <em>
                              For more info, click
                              here.
                            </em>
                          </a>
                          <br />
                          <strong>-Push Status:</strong>
                          {' '}
                          Is your Room Alert device pushing data to RoomAlert.com?
                          {' '}
                          <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href="https://avtech.com/articles/15801/"
                          >
                            <em>
                              For more info, click
                              here.
                            </em>
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </ConditionalRender>

            </fieldset>
            <ConditionalRender show={isEditingAlert}>
              <div className="mb-3 mt-5 row text-center">
                <div className="col">
                  <RaaDefaultButton
                    id="save-new-alert-button"
                    onClick={handleSaveChanges}
                    disabled={isSubmitDisabled}
                    additionalClass="mr-1"
                  >
                    Save Changes
                  </RaaDefaultButton>
                  <button
                    type="button"
                    className={`btn btn-link ${style.cancelEditBtn}`}
                    onClick={handleClearChanges}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </ConditionalRender>
          </form>
        </div>
      </ConditionalRender>
    </>
  );
}

QuickAlertForm.propTypes = {
  initialAlert: PropTypes.shape(alertPropType).isRequired,
  handleUpdateQuickAlerts: PropTypes.func.isRequired,
  errorList: PropTypes.arrayOf(PropTypes.shape({
    field: PropTypes.string,
    message: PropTypes.string,
  })).isRequired,
};

export default QuickAlertForm;
